import { useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '@/lib/supabaseClient';

export interface CeldaHora {
  dia: number;
  hora: number;
  minutos: number;
}

export interface CapacidadHora {
  dia: number;
  hora: number;
  trabajadores: number;
}

export interface TramoHorario {
  dia: number;
  inicio: string;
  fin: string;
}

interface Respuesta {
  desde: string;
  hasta: string;
  horario: TramoHorario[];
  capacidad: CapacidadHora[];
  totalCitas: number;
  celdas: CeldaHora[];
}

export const useHorasPico = (
  empresaId: string,
  mes: number,
  anio: number,
  meses: number,
  usuarioId?: string
) => {
  const [loading, setLoading] = useState(true);
  const [celdas, setCeldas] = useState<CeldaHora[]>([]);
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [horario, setHorario] = useState<TramoHorario[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [capacidad, setCapacidad] = useState<CapacidadHora[]>([]);
  const [totalCitas, setTotalCitas] = useState(0);

  useEffect(() => {
    if (!empresaId) return;
    let cancelado = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      const { data, error } = await supabase.rpc('get_horas_pico', {
        p_empresa: empresaId,
        p_mes: mes,
        p_anio: anio,
        p_meses: meses,
        p_usuario: usuarioId || null,
      });
      if (cancelado) return;

      if (error) {
        Sentry.captureException(error, { extra: { empresaId, mes, anio, meses, usuarioId } });
        console.error('get_horas_pico', error);
        setError(error.message);
        setCeldas([]);
        setHorario([]);
        setLoading(false);
        return;
      }

      const res = data as Respuesta;
      setCeldas(
        (res?.celdas ?? []).map((c) => ({
          dia: Number(c.dia),
          hora: Number(c.hora),
          minutos: Number(c.minutos),
        }))
      );
      setCapacidad(
        (Array.isArray(res?.capacidad) ? res.capacidad : []).map((c) => ({
          dia: Number(c.dia),
          hora: Number(c.hora),
          trabajadores: Number(c.trabajadores),
        }))
      );
      setTotalCitas(Number(res?.totalCitas ?? 0));
      setDesde(res?.desde ?? '');
      setHasta(res?.hasta ?? '');
      setHorario(
        (res?.horario ?? []).map((h) => ({ ...h, dia: Number(h.dia) }))
      );
      setLoading(false);
    };

    load();
    return () => {
      cancelado = true;
    };
  }, [empresaId, mes, anio, meses, usuarioId]);

  return { loading, error, celdas, desde, hasta, horario, capacidad, totalCitas };
};
